/**
 * Transcribes one rapid-fire clip (spec 02) — the same `/api/transcribe` POST-then-poll flow
 * SurveySession already runs for ordinary open-text questions, split into its own function so
 * `RapidFireVoiceRunner`'s `transcribe` prop can call it without another copy of the poll loop
 * landing inside SurveySession.tsx (already 6,000+ lines). Unlike the ordinary path, this never
 * writes into any shared component state — it only resolves or rejects with the transcript text,
 * and the caller delivers it keyed by question id (decisions-02.md 2026-08-28T18:12).
 */

const POLL_INTERVAL_MS = 1000;
const MAX_POLL_ATTEMPTS = 30; // 30s timeout, matching SurveySession's own transcription poll

function fileExtensionFor(mimeType: string): string {
  if (mimeType.includes('webm')) return '.webm';
  if (mimeType.includes('mp4')) return '.mp4';
  if (mimeType.includes('wav')) return '.wav';
  if (mimeType.includes('mp3')) return '.mp3';
  if (mimeType.includes('m4a')) return '.m4a';
  return '.webm';
}

export async function transcribeRapidFireClip(
  sessionId: number,
  questionId: string,
  blob: Blob,
): Promise<string> {
  const mimeType = blob.type || 'audio/webm';
  const formData = new FormData();
  formData.append('audio', blob, `recording${fileExtensionFor(mimeType)}`);
  formData.append('context_type', 'survey');
  formData.append('context_metadata', JSON.stringify({
    session_id: String(sessionId),
    question_id: String(questionId),
  }));

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/transcribe`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    throw new Error(`Transcription failed: ${response.status}`);
  }

  const result = await response.json();
  if (result.status !== 'processing' || !result.task_id) {
    throw new Error(result.error || 'Transcription failed - no task ID returned');
  }

  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
    const statusResponse = await fetch(
      `${import.meta.env.VITE_API_URL}/api/transcribe/status/${result.task_id}`,
    );
    if (!statusResponse.ok) {
      throw new Error('Failed to check transcription status');
    }
    const statusResult = await statusResponse.json();
    if (statusResult.status === 'completed') return statusResult.text || '';
    if (statusResult.status === 'failed') {
      throw new Error(statusResult.error || 'Transcription failed');
    }
    await new Promise((resolve) => { setTimeout(resolve, POLL_INTERVAL_MS); });
  }
  throw new Error('Transcription timeout');
}
